// src/pages/accounting/TaxReconciliationPage.jsx
// Quarterly GST/VAT reconciliation for expenses.
//
// Workflow:
//   1. Daily entry stamps each expense with an estimated tax split
//      (tax_status = 'estimated') based on the category × office defaults.
//   2. At quarter-end an admin opens this page, picks a quarter + office,
//      and reconciles against the official filing (GSTR-2B / NL VAT return).
//   3. Confirmed items move to status='confirmed'. Items still estimated past
//      the aging threshold can be bulk-reclassified back to expense (tax delta
//      posts via the standard updateExpense path).
//
// BRIO Migration: Using code "1b" (table compact)

import React, { useMemo, useState, useCallback } from 'react';
import {
  BrioAnalyticsSurface,
  BrioFrame,
  BrioByCode,
  BrioTable,
  BrioSelect,
  BrioButton,
  getTablePropsFromCode,
  useFullScreenPage,
  BrioFullScreenButton,
} from '../../components/BrioComponents';
import { useSettings } from '../../hooks/useSettings';
import {
  getDefaultTaxForCategory,
  TAX_STATUS_LABELS,
} from '../../utils/taxDefaults';

const PRESENTATION_CODE = '1b';
const AGED_THRESHOLD_DAYS = 90;

// ── Quarter helpers ────────────────────────────────────────────────────
function quarterRange(year, quarter) {
  const startMonth = (quarter - 1) * 3;
  const start = new Date(Date.UTC(year, startMonth, 1));
  const end = new Date(Date.UTC(year, startMonth + 3, 0));
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(start), to: iso(end) };
}

function ageInDays(dateStr) {
  if (!dateStr) return 0;
  const then = new Date(dateStr);
  const now = new Date();
  return Math.floor((now - then) / (1000 * 60 * 60 * 24));
}

function buildYearOptions() {
  const thisYear = new Date().getUTCFullYear();
  const years = [];
  for (let y = thisYear + 1; y >= thisYear - 4; y--) years.push(y);
  return years;
}

// ── Status derivation ──────────────────────────────────────────────────
// Pre-migration rows have no tax_* fields → 'none' (counted as non-recoverable).
function deriveTaxStatus(expense) {
  if (expense.taxStatus) return expense.taxStatus;
  if (expense.taxAmount != null && Number(expense.taxAmount) > 0) return 'estimated';
  return 'none';
}

function isRecoverable(expense) {
  if (expense.taxRecoverable === false) return false;
  if (expense.taxRecoverable === true) return true;
  return Number(expense.taxAmount || 0) > 0;
}

// ── CSV export ─────────────────────────────────────────────────────────
function downloadCsv(filename, rows, headers) {
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.map(h => escape(h.label)).join(',')];
  for (const r of rows) {
    lines.push(headers.map(h => escape(typeof h.value === 'function' ? h.value(r) : r[h.value])).join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ──────────────────────────────────────────────────────────────────────
export default function TaxReconciliationPage({
  activeProjects,
  activeExpenses,
  fmtProject,
  supabaseExpenses,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);
  const settings = useSettings();
  const formatMoney = settings.formatMoney || ((v) => String(v));
  const displayCurrency = settings.displayCurrency || 'INR';
  const convertMoney = settings.convertMoney;

  // ── Filters ──
  const today = new Date();
  const defaultQuarter = Math.floor(today.getUTCMonth() / 3) + 1;
  const [filterYear, setFilterYear] = useState(today.getUTCFullYear());
  const [filterQuarter, setFilterQuarter] = useState(defaultQuarter);
  const [filterOffice, setFilterOffice] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const { isFullScreen, toggle: toggleFullScreen, rootStyle, rootClassName } = useFullScreenPage(true);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const { from, to } = useMemo(
    () => quarterRange(filterYear, filterQuarter),
    [filterYear, filterQuarter]
  );

  // ── Filtered rows ──
  const filtered = useMemo(() => {
    const rows = [];
    for (const e of activeExpenses || []) {
      if (!e.date || e.date < from || e.date > to) continue;
      if (filterOffice && e.officeLocation !== filterOffice) continue;
      const status = deriveTaxStatus(e);
      if (filterStatus && status !== filterStatus) continue;
      rows.push({ ...e, _taxStatus: status, _ageDays: ageInDays(e.date) });
    }
    rows.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    return rows;
  }, [activeExpenses, from, to, filterOffice, filterStatus]);

  // ── Totals (in display currency) ──
  const totals = useMemo(() => {
    let recoverableConfirmed = 0;
    let recoverableEstimated = 0;
    let nonRecoverable = 0;
    let agedEstimated = 0;
    let grossTotal = 0;
    let netTotal = 0;

    for (const r of filtered) {
      const taxAmt = Number(r.taxAmount || 0);
      const gross = Number(r.amount || 0);
      const net = r.netAmount != null ? Number(r.netAmount) : gross - taxAmt;
      const taxIn = convertMoney
        ? convertMoney(taxAmt, r.currencyOriginal || 'INR', displayCurrency, r.date)
        : taxAmt;
      const grossIn = convertMoney
        ? convertMoney(gross, r.currencyOriginal || 'INR', displayCurrency, r.date)
        : gross;
      const netIn = convertMoney
        ? convertMoney(net, r.currencyOriginal || 'INR', displayCurrency, r.date)
        : net;
      grossTotal += grossIn;
      netTotal += netIn;
      const recoverable = isRecoverable(r);
      if (!recoverable || r._taxStatus === 'none') {
        nonRecoverable += taxIn;
      } else if (r._taxStatus === 'confirmed') {
        recoverableConfirmed += taxIn;
      } else if (r._taxStatus === 'estimated') {
        recoverableEstimated += taxIn;
        if (r._ageDays >= AGED_THRESHOLD_DAYS) agedEstimated += taxIn;
      }
    }
    return {
      recoverableConfirmed,
      recoverableEstimated,
      nonRecoverable,
      agedEstimated,
      grossTotal,
      netTotal,
    };
  }, [filtered, convertMoney, displayCurrency]);

  // ── Selection helpers ──
  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(r => r.id)));
  };

  // ── Actions ──
  const confirmSelected = useCallback(async () => {
    if (!supabaseExpenses?.updateExpense || selectedIds.size === 0) return;
    setBusy(true);
    setErrorMsg('');
    try {
      const targets = filtered.filter(r => selectedIds.has(r.id) && r._taxStatus === 'estimated');
      for (const r of targets) {
        const result = await supabaseExpenses.updateExpense(r.id, {
          taxStatus: 'confirmed',
          taxConfirmedAt: new Date().toISOString(),
        });
        if (!result?.success) throw new Error(result?.error || 'Update failed');
      }
      setSelectedIds(new Set());
    } catch (e) {
      setErrorMsg(e.message || 'Could not confirm tax on selected items.');
    } finally {
      setBusy(false);
    }
  }, [filtered, selectedIds, supabaseExpenses]);

  const reclassifyAged = useCallback(async () => {
    if (!supabaseExpenses?.updateExpense) return;
    const targets = filtered.filter(
      r => r._taxStatus === 'estimated' && r._ageDays >= AGED_THRESHOLD_DAYS
    );
    if (targets.length === 0) return;
    const ok = window.confirm(
      `Reclassify tax to expense for ${targets.length} aged item(s)? ` +
      `Their tax amount will be merged back into net expense.`
    );
    if (!ok) return;
    setBusy(true);
    setErrorMsg('');
    try {
      for (const r of targets) {
        const result = await supabaseExpenses.updateExpense(r.id, {
          taxStatus: 'reclassified',
          taxRecoverable: false,
          netAmount: Number(r.amount || 0),
          taxAmount: 0,
          taxRate: 0,
          taxConfirmedAt: new Date().toISOString(),
        });
        if (!result?.success) throw new Error(result?.error || 'Update failed');
      }
    } catch (e) {
      setErrorMsg(e.message || 'Could not reclassify aged items.');
    } finally {
      setBusy(false);
    }
  }, [filtered, supabaseExpenses]);

  const stampDefaults = useCallback(async () => {
    if (!supabaseExpenses?.updateExpense) return;
    const targets = filtered.filter(r => r._taxStatus === 'none');
    if (targets.length === 0) return;
    const ok = window.confirm(
      `Apply category-default tax rate to ${targets.length} item(s) with no tax info? ` +
      `Each will be marked 'estimated' for later reconciliation.`
    );
    if (!ok) return;
    setBusy(true);
    setErrorMsg('');
    try {
      for (const r of targets) {
        const def = getDefaultTaxForCategory(r.category, r.officeLocation, !!r.foreignVendor);
        if (!def || def.rate <= 0) continue;
        const gross = Number(r.amount || 0);
        const net = +(gross / (1 + def.rate)).toFixed(2);
        const tax = +(gross - net).toFixed(2);
        const result = await supabaseExpenses.updateExpense(r.id, {
          taxCode: def.code,
          taxRate: def.rate,
          taxAmount: tax,
          netAmount: net,
          taxRecoverable: def.recoverable,
          taxStatus: 'estimated',
        });
        if (!result?.success) throw new Error(result?.error || 'Update failed');
      }
    } catch (e) {
      setErrorMsg(e.message || 'Could not stamp defaults on selected items.');
    } finally {
      setBusy(false);
    }
  }, [filtered, supabaseExpenses]);

  const exportCsv = () => {
    const projectName = (id) => {
      const p = (activeProjects || []).find(x => String(x.id) === String(id));
      return p ? fmtProject(p) : '';
    };
    const headers = [
      { label: 'Date',          value: 'date' },
      { label: 'Office',        value: 'officeLocation' },
      { label: 'Project',       value: r => projectName(r.projectId) },
      { label: 'Category',      value: 'category' },
      { label: 'Description',   value: 'description' },
      { label: 'Currency',      value: r => r.currencyOriginal || 'INR' },
      { label: 'Gross',         value: 'amount' },
      { label: 'Net',           value: r => r.netAmount ?? '' },
      { label: 'Tax %',         value: r => r.taxRate ?? '' },
      { label: 'Tax Amount',    value: r => r.taxAmount ?? '' },
      { label: 'Tax Code',      value: r => r.taxCode || '' },
      { label: 'Recoverable',   value: r => (isRecoverable(r) ? 'yes' : 'no') },
      { label: 'Status',        value: '_taxStatus' },
      { label: 'Age (days)',    value: '_ageDays' },
    ];
    downloadCsv(`tax-reconciliation-${filterYear}-Q${filterQuarter}${filterOffice ? '-' + filterOffice : ''}.csv`, filtered, headers);
  };

  // ── Render ──
  const cellStyle = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0, fontSize: '10px' };

  const summaryCards = [
    { label: 'Input Tax — Confirmed',   value: totals.recoverableConfirmed, tone: 'gold' },
    { label: 'Input Tax — Estimated',   value: totals.recoverableEstimated, tone: 'pending' },
    { label: 'Aged > 90d',              value: totals.agedEstimated, tone: 'warn' },
    { label: 'Non-Recoverable Tax',     value: totals.nonRecoverable, tone: 'muted' },
    { label: 'Gross Total',             value: totals.grossTotal },
    { label: 'Net Total (P&L)',         value: totals.netTotal, tone: 'net' },
  ];

  const agedCount = filtered.filter(r => r._taxStatus === 'estimated' && r._ageDays >= AGED_THRESHOLD_DAYS).length;
  const noneCount = filtered.filter(r => r._taxStatus === 'none').length;

  return (
    <div className={`brio-flex brio-flex-col brio-analytics-font ${rootClassName}`} style={{ background: '#fff', ...rootStyle }}>
      {/* HEADER BAND */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-flex brio-items-center brio-gap-xs" style={{ flexWrap: 'nowrap' }}>
              <BrioSelect value={filterYear} onChange={e => setFilterYear(Number(e.target.value))}
                style={{ padding: '4px 8px', fontSize: '11px', minWidth: 80 }}>
                {buildYearOptions().map(y => <option key={y} value={y}>{y}</option>)}
              </BrioSelect>
              <BrioSelect value={filterQuarter} onChange={e => setFilterQuarter(Number(e.target.value))}
                style={{ padding: '4px 8px', fontSize: '11px', minWidth: 70 }}>
                {[1, 2, 3, 4].map(q => <option key={q} value={q}>{`Q${q}`}</option>)}
              </BrioSelect>
              <BrioSelect value={filterOffice} onChange={e => setFilterOffice(e.target.value)}
                style={{ padding: '4px 8px', fontSize: '11px', minWidth: 90 }}>
                <option value="">All Offices</option>
                <option value="NL">NL (VAT)</option>
                <option value="IN">IN (GST)</option>
              </BrioSelect>
              <BrioSelect value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                style={{ padding: '4px 8px', fontSize: '11px', minWidth: 130 }}>
                <option value="">All Statuses</option>
                <option value="estimated">Estimated</option>
                <option value="confirmed">Confirmed</option>
                <option value="reclassified">Reclassified</option>
                <option value="none">No tax data</option>
              </BrioSelect>
              <span className="brio-text-grey" style={{ fontSize: 10, whiteSpace: 'nowrap' }}>
                {from} → {to}
              </span>
            </div>
            {/* Direct child of the header row, so space-between pins it to the
                right edge, matching the other accounting pages. */}
            <BrioFullScreenButton isFullScreen={isFullScreen} onToggle={toggleFullScreen} />
          </div>
        </BrioFrame>
      </div>

      <BrioFrame>
        <div className="brio-flex brio-flex-col brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>

          {/* Summary cards */}
          <div className="brio-flex brio-gap-xs" style={{ flexWrap: 'wrap' }}>
            {summaryCards.map(c => {
              const color = c.tone === 'gold' ? 'var(--brio-gold,#9F902A)'
                : c.tone === 'pending' ? 'var(--brio-orange,#c87a16)'
                : c.tone === 'warn' ? 'var(--brio-red,#b9343b)'
                : c.tone === 'muted' ? 'var(--brio-grey,#888)'
                : c.tone === 'net' ? 'var(--brio-gold,#9F902A)'
                : undefined;
              return (
                <BrioAnalyticsSurface key={c.label} padding="s"
                  style={{ flex: '1 1 140px', minWidth: 140,
                    ...(c.tone === 'net' ? { borderColor: 'var(--brio-gold,#9F902A)', borderWidth: 1, borderStyle: 'solid' } : {}) }}>
                  <div className="brio-text-xs brio-text-grey">{c.label}</div>
                  <div className="brio-font-bold" style={{ fontSize: 16, color }}>
                    {formatMoney(c.value, displayCurrency)}
                  </div>
                </BrioAnalyticsSurface>
              );
            })}
          </div>

          {/* Action bar */}
          <BrioAnalyticsSurface padding="s">
            <div className="brio-flex brio-items-center brio-gap-xs" style={{ flexWrap: 'wrap' }}>
              <BrioButton variant="primary" onClick={confirmSelected}
                disabled={busy || selectedIds.size === 0}
                title="Mark selected estimated items as confirmed (after matching against the official return)">
                Confirm selected ({selectedIds.size})
              </BrioButton>
              <BrioButton variant="secondary" onClick={stampDefaults}
                disabled={busy || noneCount === 0}
                title="Apply category-default tax to expenses missing tax info (one-time cleanup)">
                Stamp defaults on no-tax items ({noneCount})
              </BrioButton>
              <BrioButton variant="danger" onClick={reclassifyAged}
                disabled={busy || agedCount === 0}
                title={`Reclassify tax to expense for items still estimated past ${AGED_THRESHOLD_DAYS} days`}>
                Reclassify aged ({agedCount})
              </BrioButton>
              <BrioButton variant="secondary" onClick={exportCsv} disabled={filtered.length === 0}>
                Export CSV
              </BrioButton>
              {errorMsg && (
                <span style={{ color: 'var(--brio-red,#b9343b)', fontSize: 11 }}>{errorMsg}</span>
              )}
            </div>
          </BrioAnalyticsSurface>

          {/* Table */}
          <BrioAnalyticsSurface padding="m">
            <BrioByCode code={PRESENTATION_CODE} className="!overflow-visible">
              <BrioTable {...tableProps} style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '3%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '4%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '17%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '4%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>
                      <input type="checkbox"
                        checked={filtered.length > 0 && selectedIds.size === filtered.length}
                        onChange={toggleAll} />
                    </th>
                    <th>Date</th>
                    <th>Off.</th>
                    <th>Project</th>
                    <th>Category</th>
                    <th>Description</th>
                    <th className="text-right">Gross</th>
                    <th className="text-right">Net</th>
                    <th className="text-right">Tax %</th>
                    <th className="text-right">Tax Amt</th>
                    <th className="text-center">Code</th>
                    <th className="text-center">Status</th>
                    <th className="text-right">Age</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={13} className="brio-text-center brio-analytics-muted" style={{ padding: '24px 0' }}>
                      No expenses found in {`Q${filterQuarter} ${filterYear}`}{filterOffice ? ` (${filterOffice})` : ''}.
                    </td></tr>
                  )}
                  {filtered.map(row => {
                    const projectName = (() => {
                      if (!row.projectId) return '—';
                      const p = (activeProjects || []).find(x => String(x.id) === String(row.projectId));
                      return p ? fmtProject(p) : `#${row.projectId}`;
                    })();
                    const recoverable = isRecoverable(row);
                    const aged = row._taxStatus === 'estimated' && row._ageDays >= AGED_THRESHOLD_DAYS;
                    const statusVariant = row._taxStatus === 'confirmed' ? 'success'
                      : row._taxStatus === 'estimated' ? 'pending'
                      : row._taxStatus === 'reclassified' ? 'info'
                      : 'pending';
                    return (
                      <tr key={row.id} className="brio-row--hoverable">
                        <td>
                          <input type="checkbox"
                            checked={selectedIds.has(row.id)}
                            onChange={() => toggleOne(row.id)} />
                        </td>
                        <td className="brio-text-grey" style={cellStyle}>{row.date || '—'}</td>
                        <td className="brio-text-grey" style={cellStyle}>{row.officeLocation || '—'}</td>
                        <td className="brio-text-grey" style={cellStyle} title={projectName}>{projectName}</td>
                        <td className="brio-text-grey" style={cellStyle}>{row.category || '—'}</td>
                        <td className="brio-text-grey" style={cellStyle} title={row.description}>
                          {row.description || '—'}
                        </td>
                        <td className="text-right brio-font-medium">
                          {formatMoney(row.amount, row.currencyOriginal || displayCurrency)}
                        </td>
                        <td className="text-right">
                          {row.netAmount != null ? formatMoney(row.netAmount, row.currencyOriginal || displayCurrency) : '—'}
                        </td>
                        <td className="text-right">{row.taxRate != null ? `${(row.taxRate * 100).toFixed(0)}%` : '—'}</td>
                        <td className="text-right" style={{ color: recoverable ? undefined : 'var(--brio-grey,#888)' }}>
                          {row.taxAmount != null ? formatMoney(row.taxAmount, row.currencyOriginal || displayCurrency) : '—'}
                        </td>
                        <td className="text-center brio-text-grey" style={{ fontSize: '9px' }} title={row.taxCode || ''}>
                          {row.taxCode || '—'}
                        </td>
                        <td className="text-center">
                          <span className={`brio-badge brio-badge--${statusVariant}`} style={{ fontSize: '8px', padding: '1px 4px' }}>
                            {TAX_STATUS_LABELS[row._taxStatus] || row._taxStatus}
                          </span>
                        </td>
                        <td className="text-right" style={{
                          fontSize: '10px',
                          color: aged ? 'var(--brio-red,#b9343b)' : 'var(--brio-grey,#888)',
                          fontWeight: aged ? 600 : 400,
                        }}>
                          {row._ageDays}d
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </BrioTable>
            </BrioByCode>

            {filtered.length > 0 && (
              <div style={{ marginTop: 8, padding: '4px 8px', fontSize: 11, display: 'flex', justifyContent: 'space-between' }}>
                <span className="brio-text-grey">{filtered.length} expenses · {selectedIds.size} selected</span>
                <span className="brio-text-grey">
                  Net input tax (recoverable, confirmed): <strong>{formatMoney(totals.recoverableConfirmed, displayCurrency)}</strong>
                </span>
              </div>
            )}
          </BrioAnalyticsSurface>
        </div>
      </BrioFrame>
    </div>
  );
}
